/**
 * Firestore user profiles (display name as username + favorite sport).
 */
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../auth/firebaseAuth';
import { isValidH2HSport } from '../constants/h2hSports';
import { logGroupInviteDebug } from '../utils/groupInviteDebug';
import { getGroupSearchErrorMessage } from '../utils/groupInviteErrors';

const MAX_AVATAR_CHARS = 450_000;

export function normalizeUsername(raw) {
  return (raw || '').trim().toLowerCase();
}

/** @returns {string | null} error message or null if valid */
export function validateUsernameFormat(raw) {
  const t = normalizeUsername(raw);
  if (t.length < 3) return 'Username must be at least 3 characters.';
  if (t.length > 20) return 'Username must be at most 20 characters.';
  if (!/^[a-z0-9_]+$/.test(t)) return 'Use lowercase letters, numbers, and underscores only.';
  return null;
}

export function normalizeDisplayNameLower(raw) {
  return (raw || '').trim().toLowerCase();
}

export function validateDisplayName(raw) {
  const t = (raw || '').trim();
  if (t.length < 3) return 'Arena name must be at least 3 characters.';
  if (t.length > 20) return 'Arena name must be 20 characters or fewer.';
  if (/\s/.test(t)) return 'Arena name cannot contain spaces.';
  if (!/^[A-Za-z0-9._-]+$/.test(t)) {
    return 'Use letters, numbers, dots, dashes, or underscores only.';
  }
  return null;
}

export function validateFavoriteSport(sport) {
  if (!sport || !String(sport).trim()) return null;
  if (!isValidH2HSport(sport)) return 'Pick a valid sport.';
  return null;
}

export function hasCompletedProfile(profile) {
  if (!profile) return false;
  return Boolean(profile.displayName?.trim() && (profile.displayNameLower || profile.usernameLower));
}

/** Lockbox-ready profile — arena name is enough; sport/avatar are enhancements. */
export function hasRealProfile(profile) {
  return hasCompletedProfile(profile);
}

/**
 * Merge profile snapshots without regressing arena-name completion.
 * Stale API/cache payloads may omit displayName after a successful save.
 */
export function mergeUserProfile(prev, next) {
  if (!next) return prev ?? null;
  if (!prev) return next;
  const merged = { ...prev };
  for (const [key, value] of Object.entries(next)) {
    if (value !== undefined) {
      merged[key] = value;
    }
  }
  if (hasCompletedProfile(prev) && !hasCompletedProfile(merged)) {
    merged.displayName = prev.displayName;
    merged.displayNameLower = prev.displayNameLower;
    merged.usernameLower = prev.usernameLower ?? prev.displayNameLower;
  }
  return merged;
}

export async function isArenaNameAvailable(displayName) {
  const displayNameLower = normalizeDisplayNameLower(displayName);
  if (!displayNameLower) return false;
  const snap = await getDoc(doc(db, 'usernames', displayNameLower));
  return !snap.exists();
}

/**
 * Step 1 — required arena name (unique via usernames collection).
 * @param {string} uid
 * @param {string} displayName
 */
export async function saveArenaUsername(uid, displayName) {
  const dnErr = validateDisplayName(displayName);
  if (dnErr) throw new Error(dnErr);

  const trimmedName = displayName.trim();
  const displayNameLower = normalizeDisplayNameLower(trimmedName);
  const available = await isArenaNameAvailable(trimmedName);
  if (!available) throw new Error('That arena name is already taken. Try another.');

  const userRef = doc(db, 'users', uid);
  // Step 1 — client-safe fields only (wallet/dispute fields are server-owned per rules).
  await setDoc(
    userRef,
    {
      displayName: trimmedName,
      displayNameLower,
      createdAt: serverTimestamp(),
    },
    { merge: true }
  );

  await setDoc(doc(db, 'usernames', displayNameLower), { uid });

  try {
    await initializeUserDefaultsOnServer();
  } catch (err) {
    console.warn('initializeUserDefaultsOnServer:', err);
  }

  return {
    id: uid,
    displayName: trimmedName,
    displayNameLower,
    favoriteSport: null,
    avatarUrl: null,
    walletBalance: 0,
    subscriptionStatus: null,
  };
}

/**
 * Step 2 — optional avatar + favorite sport (skipped allowed).
 * @param {string} uid
 * @param {{ favoriteSport?: string, avatarDataUrl?: string | null }} data
 */
export async function saveOptionalProfileExtras(uid, { favoriteSport, avatarDataUrl } = {}) {
  const sportErr = validateFavoriteSport(favoriteSport);
  if (sportErr) throw new Error(sportErr);

  const updates = {};
  if (favoriteSport && String(favoriteSport).trim()) {
    updates.favoriteSport = favoriteSport.toUpperCase();
  }

  if (avatarDataUrl && typeof avatarDataUrl === 'string' && avatarDataUrl.length > 0) {
    if (avatarDataUrl.length > MAX_AVATAR_CHARS) {
      throw new Error('Photo is too large. Choose a smaller image (under ~300KB).');
    }
    updates.avatarUrl = avatarDataUrl;
  }

  if (Object.keys(updates).length > 0) {
    await setDoc(doc(db, 'users', uid), updates, { merge: true });
  }

  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** @deprecated Use saveArenaUsername + saveOptionalProfileExtras */
export async function saveNewUserProfile(uid, { displayName, favoriteSport, avatarDataUrl }) {
  const profile = await saveArenaUsername(uid, displayName);
  return saveOptionalProfileExtras(uid, { favoriteSport, avatarDataUrl }) || profile;
}

async function initializeUserDefaultsOnServer() {
  const base = process.env.REACT_APP_CLOUD_FUNCTIONS_URL?.replace(/\/$/, '')
    || (process.env.REACT_APP_PROJECT_ID
      ? `https://${process.env.REACT_APP_FUNCTIONS_REGION || 'us-central1'}-${process.env.REACT_APP_PROJECT_ID}.cloudfunctions.net`
      : null);
  if (!base) return;
  const { auth } = await import('../auth/firebaseAuth');
  const user = auth.currentUser;
  if (!user) return;
  const idToken = await user.getIdToken();
  await fetch(`${base}/initialize_user_defaults`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({}),
  });
}

function getFunctionsBaseUrl() {
  const base = process.env.REACT_APP_CLOUD_FUNCTIONS_URL;
  if (base) return base.replace(/\/$/, '');
  const projectId = process.env.REACT_APP_PROJECT_ID;
  if (!projectId) return null;
  const region = process.env.REACT_APP_FUNCTIONS_REGION || 'us-central1';
  if (process.env.REACT_APP_USE_EMULATORS === 'true') {
    const emulatorPort = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5011';
    return `http://127.0.0.1:${emulatorPort}/${projectId}/${region}`;
  }
  return `https://${region}-${projectId}.cloudfunctions.net`;
}

async function getAuthedIdToken() {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in required.');
  return user.getIdToken();
}

export async function fetchUserProfile(uid) {
  const currentUid = auth.currentUser?.uid;
  if (currentUid && currentUid === uid) {
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  const base = getFunctionsBaseUrl();
  if (!base) return null;
  try {
    const idToken = await getAuthedIdToken();
    const res = await fetch(`${base}/get_public_user_profile?uid=${encodeURIComponent(uid)}`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (res.status === 404) return null;
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Profile lookup failed (${res.status})`);
    }
    return res.json();
  } catch (err) {
    console.warn('fetchUserProfile public lookup:', err);
    return null;
  }
}

/**
 * Real-time profile updates (subscription tier/status from webhook writes).
 * @param {string} uid
 * @param {(profile: object | null) => void} onProfile
 * @returns {() => void} unsubscribe
 */
export function subscribeUserProfile(uid, onProfile) {
  if (!uid) {
    onProfile(null);
    return () => {};
  }
  return onSnapshot(
    doc(db, 'users', uid),
    (snap) => {
      onProfile(
        snap.exists()
          ? { id: snap.id, ...snap.data() }
          : null
      );
    },
    (err) => {
      console.error('subscribeUserProfile:', err);
      onProfile(null);
    }
  );
}

/**
 * Wait until profile matches predicate (uses one-shot listener — prefer over poll loops).
 * @param {string} uid
 * @param {(profile: object | null) => boolean} predicate
 * @param {{ timeoutMs?: number }} [options]
 * @returns {Promise<object | null>}
 */
export function waitForUserProfileCondition(uid, predicate, options = {}) {
  const { timeoutMs = 30000 } = options;
  return new Promise((resolve, reject) => {
    if (!uid) {
      reject(new Error('waitForUserProfileCondition: missing uid'));
      return;
    }
    let settled = false;
    let unsub = () => {};

    const finish = (profile) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsub();
      resolve(profile);
    };

    const fail = (err) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsub();
      reject(err);
    };

    unsub = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const profile = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        if (predicate(profile)) finish(profile);
      },
      (err) => fail(err)
    );

    const timer = setTimeout(
      () => fail(new Error('waitForUserProfileCondition: timeout')),
      timeoutMs
    );
  });
}

/**
 * Find players by username prefix (for challenge opponent search).
 * @param {string} rawQuery
 * @param {string} [excludeUid] — omit current user
 */
export async function searchUsersByUsername(rawQuery, excludeUid) {
  const q = normalizeDisplayNameLower(rawQuery);
  if (q.length < 2) return [];

  const base = getFunctionsBaseUrl();
  const currentUid = auth.currentUser?.uid;
  if (!base || !currentUid) {
    throw new Error('Sign in to search for players.');
  }

  logGroupInviteDebug('search_start', { queryLength: q.length });

  try {
    const idToken = await getAuthedIdToken();
    const res = await fetch(`${base}/search_users?q=${encodeURIComponent(q)}`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const failure = new Error(err.error || `User search failed (${res.status})`);
      failure.code = res.status === 403 ? 'permission-denied' : undefined;
      throw failure;
    }
    const results = await res.json();
    const filtered = (Array.isArray(results) ? results : []).filter((p) => p.id !== excludeUid);
    logGroupInviteDebug('search_success', { resultCount: filtered.length });
    return filtered;
  } catch (err) {
    logGroupInviteDebug('search_failed', { code: err?.code || null });
    throw new Error(getGroupSearchErrorMessage(err));
  }
}

/** @deprecated Client writes blocked by rules — use applyDisputePenaltyOnServer */
export async function incrementUserRiskTally(uid, delta = 1) {
  if (!uid || typeof uid !== 'string') return;
  const inc = Number(delta) || 0;
  if (inc === 0) return;
  await updateDoc(doc(db, 'users', uid), {
    riskTally: increment(inc),
  });
}

/** @deprecated Client writes blocked by rules — use applyDisputePenaltyOnServer */
export async function setUserAccountLockUntil(uid, untilMs) {
  if (!uid || typeof uid !== 'string') return;
  const ts = Number(untilMs) || null;
  await updateDoc(doc(db, 'users', uid), {
    accountLockEndsAt: ts,
  });
}

/**
 * Server-authoritative dispute penalty — increments riskTally + sets accountLockEndsAt.
 * @param {number} untilMs
 */
export async function applyDisputePenaltyOnServer(untilMs) {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Dispute penalty API not configured.');
  const idToken = await getAuthedIdToken();
  const res = await fetch(`${base}/apply_dispute_penalty`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ untilMs }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Dispute penalty failed (${res.status})`);
  return data;
}

/**
 * Mark the guided spotlight tour / onboarding complete (client-owned field).
 * Uses merge write so missing docs / partial profiles still succeed.
 * Returns a plain patch suitable for mergeUserProfile (no raw Timestamps).
 */
export async function markOnboardingCompleted(uid) {
  if (!uid || typeof uid !== 'string') return null;
  const ref = doc(db, 'users', uid);
  await setDoc(
    ref,
    {
      onboardingCompleted: true,
      onboardingCompletedAt: serverTimestamp(),
    },
    { merge: true },
  );
  return {
    onboardingCompleted: true,
    onboardingCompletedAt: new Date().toISOString(),
  };
}
